import { ICON_USAGE } from '@brickup/shared/constants'
import type { RenderedAsset } from '@brickup/shared/dto'
import { ColorDot } from '@/components/ColorDot'
import { UsageImage } from '@/components/UsageImage'

type FaceImageProps = {
  image: RenderedAsset
  size?: number
}

/**
 * An entry's picture in the round frame every list draws it in.
 *
 * Its own component because a face is not only a product's: a measure wears
 * its first picture exactly the same way, and the product tile below is this
 * plus the colour a product falls back to.
 *
 * @single-use the picture half of `ProductSwatch`, which composes it; the one
 * outside caller is `CatalogueGlyph`, where an entry with no colour to fall back
 * to must not go through a product's tile.
 */
export function FaceImage({ image, size = 20 }: FaceImageProps) {
  return (
    <span
      className="block shrink-0 overflow-hidden rounded-full border-2 border-border"
      style={{ width: size, height: size }}
    >
      <UsageImage
        usageKey={ICON_USAGE}
        derivatives={image.derivatives}
        uuid={image.uuid}
        path={image.path}
        format={image.format}
        alt=""
        fillHeight
        fallbackSize="sm"
      />
    </span>
  )
}

type ProductSwatchProps = {
  /** Only what a tile draws. Callers that hold a partial product — a picker
   *  row, a CMS column — pass it without inventing a sort order to satisfy
   *  the type. */
  product: { hex?: string | null; cardImage?: RenderedAsset | null }
  size?: number
}

/**
 * One product as a pickable tile.
 *
 * A product carries a picture, a colour value, or neither. The picture wins
 * where there is one — it is the more informative of the two — and the colour
 * falls back to `ColorDot`, which is what every migrated colour became. A
 * product with neither still renders a tile, because a name with no tile
 * beside it reads as a broken row rather than as a plain option.
 *
 * A picture with no rendered sizes falls back to the legacy ladder the way a
 * not-yet-reprocessed asset does.
 */
export function ProductSwatch({ product, size = 20 }: ProductSwatchProps) {
  if (!product.cardImage) {
    return <ColorDot hex={product.hex} size={size} />
  }

  return <FaceImage image={product.cardImage} size={size} />
}
